import qs from 'qs';
import { httpClient, qsOptions } from 'providers/clients/httpClient';

/**
 * Core Logs API
 * The system endpoints require STAFF permission.
 */

export type CoreLogLevel =
  | 'debug'
  | 'info'
  | 'warning'
  | 'notice'
  | 'error'
  | 'fatal'
  | string;

export type CoreLogQueryField = { [key: string]: any };

export type CoreLogEntry = {
  level?: CoreLogLevel;
  time?: string | null;
  message?: string;
  fields?: {
    [key: string]: string;
  };
};

export type CoreLogListInfo = {
  log?: string;
  app?: string;
  app_label?: string;
};

export type SystemLogInfo = {
  log?: string;
};

/**
 * Paramaters and Results for API calls
 */
type LogAppListResult = {
  status?: string | number;
  data?: {
    total?: string | number;
    next?: string | number;
    prev?: string | number;
    items?: CoreLogListInfo[];
  };
};

type SystemLogListResult = {
  status?: string | number;
  data?: {
    items?: SystemLogInfo[];
  };
};

export type CoreLogSearchResult = {
  status?: string | number;
  data?: {
    total?: string | number;
    next?: string | number;
    prev?: string | number;
    items?: CoreLogEntry[];
  };
};

type GetCoreLogsAppSearchParams = {
  app: string;
  log: string;
  message?: string;
  level?: CoreLogLevel[];
  fields?: CoreLogQueryField;
  start_date?: string;
  end_date?: string;
  limit?: number;
  page?: string;
};

type GetCoreLogsSystemSearchParams = {
  log: string;
  message?: string;
  level?: CoreLogLevel[];
  fields?: CoreLogQueryField;
  start_date?: string;
  end_date?: string;
  site?: string;
  active_site?: 'yes' | 'no' | string;
  per_site_logging?: 'yes' | 'no' | string;
  limit?: number;
  page?: string;
};

/**
 * Builds the shared log-search query string (message/level/fields/date/paging).
 * Skips app/log since those are path parameters.
 **/
function buildLogSearchQuery(params: Partial<GetCoreLogsSystemSearchParams>): string {
  const {
    message,
    level,
    fields,
    start_date,
    end_date,
    site,
    active_site,
    per_site_logging,
    limit,
    page,
  } = params;

  const payload: Partial<GetCoreLogsSystemSearchParams> = {};

  if (message) {
    payload.message = message;
  }
  if (level && level.length) {
    payload.level = level;
  }
  if (fields && Object.keys(fields).length) {
    payload.fields = fields;
  }
  if (start_date) {
    payload.start_date = start_date;
  }
  if (end_date) {
    payload.end_date = end_date;
  }
  if (site) {
    payload.site = site;
  }
  if (active_site) {
    payload.active_site = active_site;
  }
  if (per_site_logging) {
    payload.per_site_logging = per_site_logging;
  }
  if (limit !== undefined && limit !== null) {
    payload.limit = limit;
  }
  if (page) {
    payload.page = page;
  }

  const hasQp = Object.keys(payload).length > 0;
  const qpStr = hasQp ? qs.stringify(payload, qsOptions) : '';
  return qpStr ? `?${qpStr}` : '';
}

// --- Provider ----------------------------------------------------------------
export const coreLogsProvider = {
  /**
   * GET /core/logs/app/list/
   * Lists available application logs
   */
  getCoreLogsAppList: async (): Promise<LogAppListResult> => {
    try {
      const { data, status } = await httpClient({
        url: '/core/logs/app/list/',
        method: 'GET',
      });

      const result: LogAppListResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getCoreLogsAppList] Error:', error);
      throw error;
    }
  },

  /**
   * GET /core/logs/system/list/
   * Lists the available system logs (STAFF only).
   */
  getCoreLogsSystemList: async (): Promise<SystemLogListResult> => {
    try {
      const { data, status } = await httpClient({
        url: '/core/logs/system/list/',
        method: 'GET',
      });

      const result: SystemLogListResult = {
        status: data?.status ?? status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getCoreLogsSystemList]', error);
      throw error;
    }
  },

  /**
   * GET /core/logs/app/search/{app}/{log}/
   * Searches an application log
   */
  getCoreLogsAppSearch: async ({
    app,
    log,
    ...rest
  }: GetCoreLogsAppSearchParams): Promise<CoreLogSearchResult> => {
    try {
      if (!app || !log) {
        throw new Error('[getCoreLogsAppSearch] Missing required app or log.');
      }

      const nApp = encodeURIComponent(app);
      const nLog = encodeURIComponent(log);
      const path = `/core/logs/app/search/${nApp}/${nLog}/`;

      const qpStr = buildLogSearchQuery(rest);

      const { data, status } = await httpClient({
        url: `${path}${qpStr}`,
        method: 'GET',
      });

      const result: CoreLogSearchResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getCoreLogsAppSearch]', error);
      throw error;
    }
  },

  /**
   * GET /core/logs/system/search/{log}/
   * Search an system log. STAFF permission required.
   */
  getCoreLogsSystemSearch: async ({
    log,
    ...rest
  }: GetCoreLogsSystemSearchParams): Promise<CoreLogSearchResult> => {
    try {
      if (!log) {
        throw new Error('[getCoreLogsSystemSearch] Missing required log.');
      }

      const nLog = encodeURIComponent(log);
      const path = `/core/logs/system/search/${nLog}/`;
      const qpStr = buildLogSearchQuery(rest);

      const { data, status } = await httpClient({
        url: `${path}${qpStr}`,
        method: 'GET',
      });

      const result: CoreLogSearchResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getCoreLogsSystemSearch]', error);
      throw error;
    }
  },
} as const;

export type CoreLogsProviderType = typeof coreLogsProvider;
